import { AlertCircle, CheckCircle2, ImageIcon, Loader2, Trash2, Video } from 'lucide-react';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { A2vAssetType, A2vUploadStatus } from '../../../model';
import type { AssetUploadQueueItem } from '../AssetUploadDialog.helpers';

interface AssetUploadQueueProps {
  items: AssetUploadQueueItem[];
  disabled: boolean;
  onRemove: (id: string) => void;
}

export function AssetUploadQueue({ items, disabled, onRemove }: AssetUploadQueueProps) {
  if (!items.length) return null;

  return (
    <div className="border-border divide-border overflow-hidden rounded-md border">
      {items.map((item) => {
        const TypeIcon = item.assetType === A2vAssetType.Video ? Video : ImageIcon;
        return (
          <div key={item.id} className="flex min-w-0 items-center gap-3 px-3 py-2.5">
            <TypeIcon className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{item.file.name}</p>
              <p className="text-muted-foreground text-xs">
                {item.width !== null && item.height !== null
                  ? `${String(item.width)} x ${String(item.height)}`
                  : dashboardT('a2v.assets.upload.readingMetadata')}
              </p>
              {item.error ? (
                <p className="text-destructive truncate text-xs" title={item.error}>
                  {item.error}
                </p>
              ) : null}
            </div>
            <QueueStatus status={item.status} />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled || item.status === A2vUploadStatus.Success}
              aria-label={dashboardT('a2v.assets.upload.remove', { name: item.file.name })}
              onClick={() => {
                onRemove(item.id);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function QueueStatus({ status }: { status: A2vUploadStatus }) {
  if (status === A2vUploadStatus.Uploading) {
    return (
      <Loader2
        className="text-muted-foreground h-4 w-4 animate-spin"
        aria-label={dashboardT('a2v.assets.upload.status.uploading')}
      />
    );
  }
  if (status === A2vUploadStatus.LoadingMetadata) {
    return (
      <Loader2
        className="text-muted-foreground h-4 w-4 animate-spin"
        aria-label={dashboardT('a2v.assets.upload.status.loadingMetadata')}
      />
    );
  }
  if (status === A2vUploadStatus.Success) {
    return (
      <CheckCircle2
        className="text-success h-4 w-4"
        aria-label={dashboardT('a2v.assets.upload.status.success')}
      />
    );
  }
  if (status === A2vUploadStatus.Error) {
    return (
      <AlertCircle
        className="text-destructive h-4 w-4"
        aria-label={dashboardT('a2v.assets.upload.status.error')}
      />
    );
  }
  return (
    <span className="text-muted-foreground text-xs">{dashboardT('a2v.assets.upload.pending')}</span>
  );
}
